/**
 * @fileoverview System-web department management page for departments, trials, and onboarding drafts.
 * @module system-web/pages/TenantsPage
 *
 * Responsibilities:
 * - Render the departments, trials, and drafts grids and side panels.
 * - Coordinate shared local-first tenant lifecycle actions such as trial extension and department activation.
 * - Subscribe to Dexie liveQuery via watchTenants() so that background sync updates (including
 *   provisioning status changes) are reflected immediately without a manual refresh.
 *
 * Dependencies:
 * - tenantService (watchTenants, listTenants, lifecycle helpers)
 * - onboardingDraftService
 * - GridPageShell and DataGrid UI primitives
 *
 * Architecture:
 * - Local-first. Grid and panel state comes from Dexie-backed services via a reactive liveQuery
 *   subscription. Lifecycle routes reconcile authoritative server state back into Dexie;
 *   liveQuery ensures the UI reflects those changes automatically.
 *
 * Notes:
 * - Trial lifecycle mutations should stay centralized in tenantService instead of page-local API logic.
 * - The watchTenants subscription also keeps selectedTenant in sync: when sync or server reconciliation
 *   updates a tenant row in Dexie, the open panel updates immediately without requiring a row re-click.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, FileEdit, Trash2, Clock, ArrowRightCircle, AlertTriangle, Sparkles, Building2, ExternalLink, Download, UserX, Eye, Pencil, CheckCircle2, Flame, LayoutGrid, RefreshCw, LogIn, RefreshCcw, Smartphone, Monitor, Maximize2, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { watchTenants, listTenants, listTrials, syncTrialsFromServer, upsertTenant, deleteTenantLocal, convertTenantTrialToActive, extendTenantTrialPeriod, updateTenantStatusOnServer, deleteTenantOnServer, cleanupExpiredTrialOnServer, impersonateTenant, mobileImpersonateTenant } from '../services/tenantService';
import { syncLookupsToTenant } from '../services/lookupService';
import { listOnboardingDrafts, deleteOnboardingDraft, migrateLegacyDrafts } from '../services/onboardingDraftService';
import ConfirmDeleteModal from '../components/ui/ConfirmDeleteModal';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import { openAsyncUrlWithPopupFallback } from '../utils/browserNavigation';
import { applyJurisdictionToAddress } from '../utils/jurisdictionFips';
import type { GridPageShellHandle } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction, BulkAction, GridTemplate } from '@ui-admin/components/DataGrid';
import DepartmentPanel, { type PanelTab } from './DepartmentPanel';
import TrialPanel from './TrialPanel';
import DraftPanel from './DraftPanel';
import EditDepartmentModal from './EditDepartmentModal';
import { calculateReadiness } from './tenants/wizard/readiness';
import { useTheme } from '@ui-admin/contexts/ThemeContext';

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Tenant reads come from Dexie-backed services and lifecycle actions optimistically update local state before reconciliation.
 *
 * Sync Strategy:
 * - Shared tenantService helpers call authoritative lifecycle routes and then reconcile Dexie-backed state.
 *
 * Conflict Resolution:
 * - Server lifecycle responses win after local optimistic state is applied.
 */

/**
 * @errorHandling
 *
 * Strategy:
 * - Capture page-level action failures, keep the current screen open, and surface the failure in the action error banner.
 *
 * User Impact:
 * - Users remain in context and can retry extension, activation, or cleanup actions.
 *
 * Logging:
 * - REQUIRED
 */

// ============================================================
// Constants
// ============================================================

const TIER_COLORS: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  demo: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-gray-100 text-gray-600',
};

function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

// ============================================================
// Column definitions
// ============================================================

const COLUMNS: DataGridColumn<any>[] = [
  {
    id: 'name',
    header: 'Department',
    accessorKey: 'name',
    sortable: true,
    filterable: true,
    minWidth: 200,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 mt-0.5 h-8 w-8 rounded-lg bg-orange-50 flex items-center justify-center">
            <Flame className="h-4 w-4 text-orange-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
              {row.name}
              <a
                href={`/tenants/${row._id}`}
                title={`Open full page for ${row.name}`}
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 text-gray-400 hover:text-orange-600 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
            <p className="text-xs text-gray-400 truncate">{row.slug || '—'}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: 'tier',
    header: 'Tier',
    accessorKey: 'tier',
    sortable: true,
    filterable: true,
    width: 110,
    cell: (info) => {
      const v = info.getValue() as string;
      return v ? (
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>
          {v}
        </span>
      ) : <span className="text-gray-400">—</span>;
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    sortable: true,
    filterable: true,
    width: 110,
    cell: (info) => {
      const v = info.getValue() as string;
      return v ? (
        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${STATUS_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>
          {v}
        </span>
      ) : <span className="text-gray-400">—</span>;
    },
  },
  {
    id: 'departmentType',
    header: 'Type',
    accessorKey: 'departmentType',
    sortable: true,
    filterable: true,
    width: 130,
    cell: (info) => <span className="text-sm text-gray-600">{(info.getValue() as string) || '—'}</span>,
  },
  {
    id: 'database',
    header: 'Database',
    accessorFn: (row: any) => row.dataLocation?.databaseName || '',
    sortable: false,
    width: 170,
    cell: (info) => (
      <span className="font-mono text-xs text-gray-400">{(info.getValue() as string) || '—'}</span>
    ),
  },
  {
    id: 'renewalDate',
    header: 'Renewal',
    accessorFn: (row: any) => row.billing?.subscription?.renewalDate || '',
    sortable: true,
    width: 110,
    cell: (info) => {
      const v = info.getValue() as string;
      return <span className="text-sm text-gray-500">{v ? new Date(v).toLocaleDateString() : '—'}</span>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Last Activity',
    accessorKey: 'updatedAt',
    sortable: true,
    width: 120,
    cell: (info) => {
      const v = info.getValue() as string;
      if (!v) return <span className="text-sm text-gray-300">—</span>;
      return (
        <div>
          <p className="text-sm text-gray-600">{new Date(v).toLocaleDateString()}</p>
          <p className="text-[10px] text-gray-400">{formatRelativeDate(v)}</p>
        </div>
      );
    },
  },
  {
    id: 'organization',
    header: 'Organization',
    accessorFn: (row: any) => row.organizationName || row.organizationId || '',
    sortable: true,
    filterable: true,
    width: 160,
    hidden: true,
    cell: (info) => <span className="text-sm text-gray-600 truncate">{(info.getValue() as string) || '—'}</span>,
  },
  {
    id: 'createdAt',
    header: 'Created',
    accessorKey: 'createdAt',
    sortable: true,
    width: 110,
    hidden: true,
    cell: (info) => {
      const v = info.getValue() as string;
      return <span className="text-sm text-gray-500">{v ? new Date(v).toLocaleDateString() : '—'}</span>;
    },
  },
];

// ============================================================
// Filters
// ============================================================

const FILTERS: DataGridFilter[] = [
  {
    id: 'tier',
    label: 'Tier',
    type: 'status',
    options: [
      { value: 'professional', label: 'Professional', color: 'blue' },
      { value: 'advanced', label: 'Advanced', color: 'purple' },
      { value: 'premium', label: 'Premium', color: 'amber' },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    type: 'status',
    options: [
      { value: 'active', label: 'Active', color: 'green' },
      { value: 'suspended', label: 'Suspended', color: 'red' },
      { value: 'demo', label: 'Demo', color: 'yellow' },
      { value: 'expired', label: 'Expired', color: 'gray' },
    ],
  },
  {
    id: 'departmentType',
    label: 'Type',
    type: 'select',
    options: [
      { value: 'Fire Department', label: 'Fire Department' },
      { value: 'Fire District', label: 'Fire District' },
      { value: 'Fire Authority', label: 'Fire Authority' },
      { value: 'County Fire', label: 'County Fire' },
      { value: 'Municipal Fire', label: 'Municipal Fire' },
    ],
  },
];

// ============================================================
// Seed templates
// ============================================================

const SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'departments-default-template',
    gridType: 'departments',
    name: 'Default View',
    description: 'Standard departments layout with all columns.',
    state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'name', desc: false }], filterValues: {}, pageSize: 25, searchText: '' },
    scope: 'system',
    createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' },
    isDefault: true, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    _id: 'departments-active-template',
    gridType: 'departments',
    name: 'Active Departments',
    description: 'Shows only active departments sorted by name.',
    state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'name', desc: false }], filterValues: { status: ['active'] }, pageSize: 25, searchText: '' },
    scope: 'system',
    createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' },
    isDefault: false, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    _id: 'departments-expiring-template',
    gridType: 'departments',
    name: 'Expiring Soon',
    description: 'Active departments sorted by renewal date (soonest first).',
    state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'renewalDate', desc: false }], filterValues: { status: ['active'] }, pageSize: 25, searchText: '' },
    scope: 'system',
    createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' },
    isDefault: false, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ============================================================
// Trial columns (used in Trials tab DataGrid)
// ============================================================

const TRIAL_COLUMNS: DataGridColumn<any>[] = [
  {
    id: 'name', header: 'Department', accessorKey: 'name', sortable: true, filterable: true, minWidth: 200,
    cell: (info) => {
      const row = info.row.original;
      const isExpired = row._trialExpired;
      const daysLeft = row._trialDaysRemaining ?? 0;
      return (
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 mt-0.5 h-8 w-8 rounded-lg bg-orange-50 flex items-center justify-center">
            <Clock className="h-4 w-4 text-orange-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
            <p className="text-xs text-gray-400 font-mono truncate">{row.slug || '—'}</p>
          </div>
          {isExpired
            ? <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full shrink-0"><AlertTriangle className="h-3 w-3" /> Expired</span>
            : daysLeft <= 7
              ? <span className="ml-1 inline-flex px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full shrink-0">{daysLeft}d left</span>
              : <span className="ml-1 inline-flex px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-600 rounded-full shrink-0">{daysLeft}d left</span>
          }
        </div>
      );
    },
  },
  {
    id: 'tier', header: 'Tier', accessorKey: 'tier', sortable: true, width: 110,
    cell: (info) => { const v = info.getValue() as string; return v ? <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span> : <span className="text-gray-400">—</span>; },
  },
  {
    id: 'trialEndDate', header: 'End Date', accessorFn: (row: any) => row.trial?.endDate || '', sortable: true, width: 110,
    cell: (info) => { const v = info.getValue() as string; return <span className="text-sm text-gray-600">{v ? new Date(v).toLocaleDateString() : '—'}</span>; },
  },
  {
    id: 'extensions', header: 'Extensions', accessorFn: (row: any) => row.trial?.extensions?.length ?? 0, sortable: true, width: 100,
    cell: (info) => { const n = info.getValue() as number; return <span className="text-sm text-gray-600">{n > 0 ? `${n}x` : <span className="text-gray-300">—</span>}</span>; },
  },
  {
    id: 'outcome', header: 'Outcome', accessorFn: (row: any) => row.trial?.outcome || '', sortable: true, width: 110,
    cell: (info) => {
      const v = info.getValue() as string;
      if (v === 'converted') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full"><Sparkles className="h-3 w-3" /> Converted</span>;
      return <span className="text-sm text-gray-400 capitalize">{v || '—'}</span>;
    },
  },
  {
    id: 'trialStartDate', header: 'Start Date', accessorFn: (row: any) => row.trial?.startDate || '', sortable: true, width: 110, hidden: true,
    cell: (info) => { const v = info.getValue() as string; return <span className="text-sm text-gray-500">{v ? new Date(v).toLocaleDateString() : '—'}</span>; },
  },
];

// ============================================================
// Draft columns (used in Drafts tab DataGrid)
// ============================================================

const DRAFT_COLUMNS: DataGridColumn<any>[] = [
  {
    id: 'name', header: 'Draft Name', accessorFn: (row: any) => row.wizardState?.departmentInfo?.name || 'Untitled Draft', sortable: true, filterable: true, minWidth: 200,
    cell: (info) => {
      const row = info.row.original;
      const name = row.wizardState?.departmentInfo?.name || 'Untitled Draft';
      return (
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 mt-0.5 h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center">
            <FileEdit className="h-4 w-4 text-gray-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            <p className="text-xs text-gray-400 font-mono truncate">{row._id}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: 'lastStep', header: 'Progress', accessorKey: 'lastStep', sortable: true, width: 110,
    cell: (info) => { const n = info.getValue() as number; return <span className="text-sm text-gray-600">Step {n ?? 1} of 9</span>; }, // 9-step wizard
  },
  {
    id: 'readiness', header: 'Readiness', accessorFn: (row: any) => {
      try { return calculateReadiness(row.wizardState ?? {}).score; } catch { return 0; }
    }, sortable: true, width: 110,
    cell: (info) => {
      const score = info.getValue() as number;
      const color = score === 100 ? 'bg-green-500' : score >= 60 ? 'bg-amber-400' : 'bg-red-400';
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` } as React.CSSProperties} />
          </div>
          <span className="text-xs text-gray-600 w-8 text-right">{score}%</span>
        </div>
      );
    },
  },
  {
    id: 'updatedAt', header: 'Last Updated', accessorKey: 'updatedAt', sortable: true, width: 130,
    cell: (info) => { const v = info.getValue() as string; return <span className="text-sm text-gray-500">{v ? new Date(v).toLocaleDateString() : '—'}</span>; },
  },
  {
    id: 'createdAt', header: 'Created', accessorKey: 'createdAt', sortable: true, width: 110, hidden: true,
    cell: (info) => { const v = info.getValue() as string; return <span className="text-sm text-gray-500">{v ? new Date(v).toLocaleDateString() : '—'}</span>; },
  },
];

// ============================================================
// TenantsPage
// ============================================================

/**
 * @component TenantsPage
 *
 * Purpose:
 * - Provide system administrators and sales users a unified workspace for department, trial, and draft management.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - Local-first. Grid and panel state comes from Dexie-backed services via watchTenants liveQuery
 *   subscription. Lifecycle routes reconcile authoritative server state back into Dexie, and the
 *   liveQuery fires automatically to propagate those changes to the grid and any open panel.
 *
 * Constraints:
 * - Trial extension and activation must flow through shared service actions, not page-specific API mutations.
 *
 * Performance:
 * - Uses watchTenants() liveQuery for reactive grid updates and page-local selection state for panel.
 * - selectedTenant is derived from the live tenants array on each subscription emission so the panel
 *   always shows the latest provisioning status, billing state, or any other server-reconciled change.
 */
export default function TenantsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeId } = useTheme();

  // Data
  const [tenants, setTenants] = useState<any[]>([]);
  const [trials, setTrials] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);

  // Page tab (Departments / Trials / Drafts)
  const [tab, setTab] = useState<'departments' | 'trials' | 'drafts'>('departments');

  // Refs for imperative access (Templates/Refresh buttons in tab bar)
  const deptGridRef = useRef<GridPageShellHandle>(null);
  const trialsGridRef = useRef<GridPageShellHandle>(null);
  const draftsGridRef = useRef<GridPageShellHandle>(null);
  const activeGridRef = tab === 'departments' ? deptGridRef : tab === 'trials' ? trialsGridRef : draftsGridRef;

  // Slide-out panel
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const selectedTenantRef = useRef<any | null>(null);
  const [panelTab, setPanelTab] = useState<PanelTab>('overview');

  // Row-level action modals
  const [enteringDept, setEnteringDept] = useState<string | null>(null);
  const [enteringMobile, setEnteringMobile] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState<any[] | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<any | null>(null);
  const [activateTarget, setActivateTarget] = useState<any | null>(null);
  const [bulkSuspendTargets, setBulkSuspendTargets] = useState<any[] | null>(null);

  // Slide-out panels for Trials and Drafts
  const [selectedTrial, setSelectedTrial] = useState<any | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<any | null>(null);

  useEffect(() => {
    selectedTenantRef.current = selectedTenant;
  }, [selectedTenant]);

  // Trial modals
  const [extendTarget, setExtendTarget] = useState<{ id: string; name: string } | null>(null);
  const [extendDays, setExtendDays] = useState(14);
  const [extendReason, setExtendReason] = useState('');
  const [convertTarget, setConvertTarget] = useState<{ id: string; name: string } | null>(null);
  const [cleanupTarget, setCleanupTarget] = useState<{ id: string; name: string } | null>(null);

  const [clearingDrafts, setClearingDrafts] = useState(false);

  // Inline error banner (replaces alert())
  const [actionError, setActionError] = useState<string | null>(null);

  // Lookup sync state
  const [syncingLookupsTenantId, setSyncingLookupsTenantId] = useState<string | null>(null);
  const [syncLookupBanner, setSyncLookupBanner] = useState<{ name: string; counts: string } | null>(null);

  // ── Data loaders ──

  const loadTenants = useCallback(() => {
    listTenants().then(setTenants).catch(() => setTenants([]));
  }, []);

  const loadTrials = useCallback(() => {
    listTrials().then(setTrials).catch(() => setTrials([]));
  }, []);

  const loadDrafts = useCallback(() => {
    listOnboardingDrafts().then(setDrafts).catch(() => setDrafts([]));
  }, []);

  // ── Reactive tenants subscription ──────────────────────────────────────────
  // watchTenants() wraps the Dexie tenants table in a liveQuery so the grid
  // and any open side-panel update automatically when:
  //   • background sync pulls a status change (e.g. provisioning → active)
  //   • refreshTenantFromServer writes a fresh server record into Dexie
  //   • optimistic writes from upsertTenant / retryProvisioning fire
  // The selectedTenant derivation ensures the open DepartmentPanel always
  // reflects the latest Dexie state without a row re-click.
  useEffect(() => {
    let cancelled = false;
    const subscription = watchTenants().subscribe({
      next: (fresh) => {
        if (cancelled) return;
        setTenants(fresh);
        setSelectedTenant((prev: any | null) => {
          if (!prev) return prev;
          const updated = fresh.find((t: any) => t._id === prev._id);
          // Return null (close panel) if the tenant was deleted via sync pipeline.
          return updated ?? null;
        });
      },
      error: () => {
        if (!cancelled) setTenants([]);
      },
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    loadTrials();
    loadDrafts();
  }, []);

  // ── Legacy draft-id migration (one-time mount effect) ─────────────────────
  // Migrate legacy UUID-shaped draft ids to ObjectId-shaped ids. This writes
  // to Dexie, so it MUST run from a mount effect — never from inside a
  // liveQuery observed function (see livequery-contract.md, G-009).
  useEffect(() => {
    void migrateLegacyDrafts().catch((err) => {
      console.warn('[TenantsPage] migrateLegacyDrafts failed:', err);
    });
  }, []);

  // Reload data when navigating back from wizard with a created tenant
  useEffect(() => {
    const state = location.state as { created?: string } | null;
    if (state?.created) {
      // Reload all data to pick up the newly created tenant/trial
      loadTenants();
      loadTrials();
      loadDrafts();
      // Clear the state so it doesn't reload on every render
      navigate('/tenants', { replace: true, state: {} });
    }
  }, [location.state]);

  // Auto-refresh trials when on the trials tab (picks up new trials from sync)
  useEffect(() => {
    if (tab !== 'trials') return;
    
    const interval = setInterval(() => {
      loadTrials();
    }, 3000); // Refresh every 3 seconds when viewing trials tab
    
    return () => clearInterval(interval);
  }, [tab, loadTrials]);

  // ── Row action handlers ──

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    try {
      const updated = { ...suspendTarget, status: 'suspended' };
      await upsertTenant(updated);
      setTenants((ts) => ts.map((t) => t._id === updated._id ? updated : t));
      if (selectedTenant?._id === updated._id) setSelectedTenant(updated);
      setSuspendTarget(null);
      updateTenantStatusOnServer(suspendTarget._id, 'suspended').catch((err) => {
        console.warn('[TenantsPage] Failed to sync tenant suspend to server:', err);
      });
    } catch (err: any) {
      setActionError(err.message || 'Failed to suspend department');
      setSuspendTarget(null);
    }
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    try {
      const updated = { ...activateTarget, status: 'active' };
      await upsertTenant(updated);
      setTenants((ts) => ts.map((t) => t._id === updated._id ? updated : t));
      if (selectedTenant?._id === updated._id) setSelectedTenant(updated);
      setActivateTarget(null);
      updateTenantStatusOnServer(activateTarget._id, 'active').catch((err) => {
        console.warn('[TenantsPage] Failed to sync tenant activate to server:', err);
      });
    } catch (err: any) {
      setActionError(err.message || 'Failed to activate department');
      setActivateTarget(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTenantLocal(deleteTarget._id);
      setTenants((ts) => ts.filter((t) => t._id !== deleteTarget._id));
      if (selectedTenant?._id === deleteTarget._id) setSelectedTenant(null);
      setDeleteTarget(null);
      deleteTenantOnServer(deleteTarget._id).catch((err) => {
        if (!(err instanceof TypeError)) {
          console.error('[TenantsPage] Server rejected delete — record may reappear on next sync:', err.message);
        }
      });
    } catch (err: any) {
      setActionError(err.message || 'Failed to delete department');
      setDeleteTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteTargets) return;
    try {
      await Promise.all(bulkDeleteTargets.map((t) => deleteTenantLocal(t._id)));
      setTenants((ts) => ts.filter((t) => !bulkDeleteTargets.some((bt) => bt._id === t._id)));
      if (selectedTenant && bulkDeleteTargets.some((bt) => bt._id === selectedTenant._id)) setSelectedTenant(null);
      setBulkDeleteTargets(null);
      Promise.all(bulkDeleteTargets.map((t) => deleteTenantOnServer(t._id))).catch((err) => {
        if (!(err instanceof TypeError)) {
          console.error('[TenantsPage] Server rejected bulk delete — records may reappear on next sync:', err.message);
        }
      });
    } catch (err: any) {
      setActionError(err.message || 'Failed to delete selected departments');
      setBulkDeleteTargets(null);
    }
  };

  const handleBulkSuspend = async () => {
    if (!bulkSuspendTargets) return;
    try {
      await Promise.all(bulkSuspendTargets.map((t) => upsertTenant({ ...t, status: 'suspended' })));
      setTenants((ts) => ts.map((t) => bulkSuspendTargets.some((bt) => bt._id === t._id) ? { ...t, status: 'suspended' } : t));
      setBulkSuspendTargets(null);
      Promise.all(bulkSuspendTargets.map((t) => updateTenantStatusOnServer(t._id, 'suspended'))).catch((err) => {
        console.warn('[TenantsPage] Failed to sync bulk tenant suspend to server:', err);
      });
    } catch (err: any) {
      setActionError(err.message || 'Failed to suspend selected departments');
      setBulkSuspendTargets(null);
    }
  };

  // ── Sync Lookups ──

  const handleSyncLookups = useCallback(async (row: any) => {
    setSyncingLookupsTenantId(row._id);
    setSyncLookupBanner(null);
    try {
      const result = await syncLookupsToTenant(row._id);
      setSyncLookupBanner({
        name: row.name,
        counts: `${result.systemLookups} values · ${result.systemCategories} categories · ${result.systemGroups} groups`,
      });
      setTimeout(() => setSyncLookupBanner(null), 6000);
    } catch (err: any) {
      setActionError(err.message || 'Failed to sync lookups');
    } finally {
      setSyncingLookupsTenantId(null);
    }
  }, []);

  // ── Row actions ──

  const handleEnterDepartment = useCallback(async (row: any) => {
    setEnteringDept(row._id);
    try {
      await openAsyncUrlWithPopupFallback(
        async () => {
          const result = await impersonateTenant(row._id, themeId);
          return result.redirectUrl;
        },
        {
          blockedMessage: 'Safari or your browser blocked opening the tenant in a new tab. Select OK to continue in this tab, or Cancel to stay on the department list.',
        },
      );
    } catch (err: any) {
      setActionError(err.message || 'Failed to enter department');
    } finally {
      setEnteringDept(null);
    }
  }, [themeId]);

  const handleEnterMobile = useCallback(async (row: any) => {
    setEnteringMobile(row._id);
    try {
      await openAsyncUrlWithPopupFallback(
        async () => {
          const result = await mobileImpersonateTenant(row._id, themeId);
          return result.redirectUrl;
        },
        {
          blockedMessage: 'Safari or your browser blocked opening the mobile app in a new tab. Select OK to continue in this tab, or Cancel to stay on the department list.',
        },
      );
    } catch (err: any) {
      setActionError(err.message || 'Failed to enter mobile app');
    } finally {
      setEnteringMobile(null);
    }
  }, [themeId]);

  const ROW_ACTIONS: DataGridRowAction<any>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: Eye,
      onClick: (row) => { setSelectedTenant(row); setPanelTab('overview'); },
    },
    {
      id: 'enter',
      label: 'Enter Tenant Web',
      icon: Monitor,
      onClick: (row) => handleEnterDepartment(row),
    },
    {
      id: 'enter-mobile',
      label: 'Enter Tenant Mobile',
      icon: Smartphone,
      onClick: (row) => handleEnterMobile(row),
    },
    {
      id: 'edit',
      label: 'Edit Department',
      icon: Pencil,
      onClick: (row) => setEditTarget(row),
    },
    {
      id: 'suspend',
      label: 'Suspend',
      icon: UserX,
      onClick: (row) => setSuspendTarget(row),
      hidden: (row) => row.status === 'suspended',
    },
    {
      id: 'activate',
      label: 'Activate',
      icon: CheckCircle2,
      onClick: (row) => setActivateTarget(row),
      hidden: (row) => row.status === 'active',
    },
    {
      id: 'sync-lookups',
      label: 'Update Lookups',
      icon: RefreshCcw,
      onClick: (row) => handleSyncLookups(row),
      hidden: (row) => syncingLookupsTenantId === row._id,
    },
    {
      id: 'delete',
      label: 'Delete Department',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (row) => setDeleteTarget(row),
    },
  ];

  // ── Trial row actions ──

  const TRIAL_ROW_ACTIONS: DataGridRowAction<any>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: Eye,
      onClick: (row) => { setSelectedTenant(row); setPanelTab('trial'); },
    },
    {
      id: 'extend',
      label: 'Extend Trial',
      icon: Clock,
      onClick: (row) => setExtendTarget({ id: row._id, name: row.name }),
      hidden: (row) => row.trial?.outcome === 'converted',
    },
    {
      id: 'convert',
      label: 'Convert to Active',
      icon: ArrowRightCircle,
      onClick: (row) => setConvertTarget({ id: row._id, name: row.name }),
      hidden: (row) => row._trialExpired || row.trial?.outcome === 'converted',
    },
    {
      id: 'cleanup',
      label: 'Clean Up',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (row) => setCleanupTarget({ id: row._id, name: row.name }),
      hidden: (row) => !row._trialExpired || row.trial?.outcome === 'converted',
    },
  ];

  // ── Draft row actions ──

  const DRAFT_ROW_ACTIONS: DataGridRowAction<any>[] = [
    {
      id: 'resume',
      label: 'Resume Draft',
      icon: ExternalLink,
      onClick: (row) => navigate(`/tenants/new/${row._id}`),
    },
    {
      id: 'delete',
      label: 'Delete Draft',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: async (row) => {
        try {
          await deleteOnboardingDraft(row._id);
          if (selectedDraft?._id === row._id) setSelectedDraft(null);
          loadDrafts();
        } catch (err: any) {
          setActionError(err.message || 'Failed to delete draft');
        }
      },
    },
  ];

  // ── Bulk actions ──

  const BULK_ACTIONS: BulkAction<any>[] = [
    {
      id: 'export',
      label: 'Export Selected',
      icon: Download,
      onClick: (rows) => console.log('Export', rows.length, 'departments'),
    },
    {
      id: 'suspend',
      label: 'Suspend Selected',
      icon: UserX,
      onClick: (rows) => setBulkSuspendTargets(rows),
    },
    {
      id: 'delete',
      label: 'Delete Selected',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (rows) => setBulkDeleteTargets(rows),
    },
  ];

  // ── Trial handlers ──

  const handleExtend = async () => {
    if (!extendTarget) return;
    try {
      const updated = await extendTenantTrialPeriod(extendTarget.id, extendDays, extendReason);
      setExtendTarget(null);
      setExtendDays(14);
      setExtendReason('');
      if (updated) {
        if (selectedTenant?._id === extendTarget.id) {
          setSelectedTenant(updated);
        }
        if (selectedTrial?._id === extendTarget.id) {
          setSelectedTrial(updated);
        }
        setTenants((current) => current.map((tenant) => tenant._id === extendTarget.id ? updated : tenant));
        setTrials((current) => current.map((tenant) => tenant._id === extendTarget.id ? updated : tenant));
      }

      const refreshedTrials = await listTrials();
      setTrials(refreshedTrials);
      loadTenants();
    } catch (err: any) {
      setActionError(err.message || 'Failed to extend trial');
    }
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    const tenantId = convertTarget.id;

    try {
      const updated = await convertTenantTrialToActive(tenantId);

      if (updated) {
        const currentTenant = selectedTenantRef.current;
        if (currentTenant?._id === tenantId) {
          setSelectedTenant(updated);
        }
        if (selectedTrial?._id === tenantId) {
          setSelectedTrial(updated);
        }
        setTenants((current) => current.map((tenant) => tenant._id === tenantId ? updated : tenant));
        setTrials((current) => current.filter((tenant) => tenant._id !== tenantId));
      }

      setConvertTarget(null);

      const refreshedTrials = await listTrials();
      setTrials(refreshedTrials);
      loadTenants();
    } catch (err: any) {
      setActionError(err.message || 'Failed to convert trial');
      setConvertTarget(null);
    }
  };

  const handleCleanup = async () => {
    if (!cleanupTarget) return;
    try {
      await deleteTenantLocal(cleanupTarget.id);
      setTenants((ts) => ts.filter((t) => t._id !== cleanupTarget.id));
      setCleanupTarget(null);
      loadTrials();
      loadTenants();
      cleanupExpiredTrialOnServer(cleanupTarget.id)
        .then(() => { loadTrials(); loadTenants(); })
        .catch((err) => {
          console.warn('[TenantsPage] Failed to sync trial cleanup to server:', err);
        });
    } catch (err: any) {
      setActionError(err.message || 'Failed to clean up trial');
      setCleanupTarget(null);
    }
  };

  const handleClearAllDrafts = async () => {
    try {
      await Promise.all(drafts.map((d) => deleteOnboardingDraft(d._id)));
      setSelectedDraft(null);
      setClearingDrafts(false);
      loadDrafts();
    } catch (err: any) {
      setActionError(err.message || 'Failed to clear drafts');
      setClearingDrafts(false);
    }
  };

  // ── Panel open/close triggered from Trial tab inside panel ──

  const handlePanelExtend = (id: string, name: string) => setExtendTarget({ id, name });
  const handlePanelConvert = (id: string, name: string) => setConvertTarget({ id, name });
  const handlePanelCleanup = (id: string, name: string) => setCleanupTarget({ id, name });

  // ── Inline-edit save handler ──

  const handlePanelSave = useCallback(async (fieldKey: string, value: unknown) => {
    if (!selectedTenantRef.current) return;

    function deepSet(obj: any, path: string, val: unknown): any {
      const keys = path.split('.');
      if (keys.length === 1) return { ...obj, [keys[0]]: val };
      const key = keys[0];
      return { ...obj, [key]: deepSet(obj[key] ?? {}, keys.slice(1).join('.'), val) };
    }

    let saveValue = value;
    if (fieldKey === 'branding.backgroundOpacity' && typeof value === 'number') {
      saveValue = value / 100;
    }

    const prev = selectedTenantRef.current;
    let updated = deepSet({ ...prev }, fieldKey, saveValue);

    // When the jurisdiction object is saved (State/County lookup selectors),
    // derive address.state and address.county from the jurisdiction so the
    // Address section stays consistent with the single source of truth and the
    // geo-cache parcel matcher has the county name without a duplicate field.
    if (fieldKey === 'jurisdiction' && saveValue && typeof saveValue === 'object') {
      updated = applyJurisdictionToAddress(updated, saveValue as Record<string, any>);
    }

    selectedTenantRef.current = updated;
    setSelectedTenant(updated);
    setTenants((ts: any[]) => ts.map((t: any) => t._id === updated._id ? updated : t));

    try {
      await upsertTenant(updated);
    } catch (err: any) {
      selectedTenantRef.current = prev;
      setSelectedTenant(prev);
      setTenants((ts: any[]) => ts.map((t: any) => t._id === prev._id ? prev : t));
      console.error('Failed to save field', fieldKey, err);
    }
  }, []);

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="flex flex-col h-full">
      {/* Error banner */}
      {actionError && (
        <div className="mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="shrink-0 text-red-400 hover:text-red-600 ml-2">✕</button>
        </div>
      )}

      {/* Lookup sync success banner */}
      {syncLookupBanner && (
        <div className="mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 shrink-0">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span className="flex-1"><strong>{syncLookupBanner.name}</strong> lookups updated — {syncLookupBanner.counts}</span>
          <button onClick={() => setSyncLookupBanner(null)} className="shrink-0 text-green-400 hover:text-green-600 ml-2">✕</button>
        </div>
      )}

      {/* Page header */}
      <div className="px-6 pt-4 pb-0 shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
        <p className="mt-0.5 text-sm text-gray-500">Fire departments and inspection agencies</p>
      </div>

      {/* Page-level tab bar + Templates/Refresh buttons in one row */}
      <div className="flex items-center justify-between mx-6 mt-3 mb-0 shrink-0">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {(['departments', 'trials', 'drafts'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t === 'departments' ? `Departments (${tenants.length})` : t === 'trials' ? `Trials (${trials.length})` : `Drafts (${drafts.length})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => activeGridRef.current?.openTemplates()}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <LayoutGrid className="h-4 w-4" /> Templates
          </button>
          <button
            onClick={async () => {
              if (tab === 'departments') loadTenants();
              else if (tab === 'trials') {
                // Sync from server to get fresh data
                const fresh = await syncTrialsFromServer();
                setTrials(fresh);
              }
              else loadDrafts();
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* ── Departments tab — GridPageShell ── */}
      {tab === 'departments' && (
        <div className="flex-1 min-h-0">
          <GridPageShell
            ref={deptGridRef}
            gridId="departments"
            gridType="departments"
            title="Departments"
            subtitle="Fire departments and inspection agencies"
            hideHeader
            columns={COLUMNS}
            data={tenants}
            getRowId={(t: any) => t._id}
            filters={FILTERS}
            rowActions={ROW_ACTIONS}
            bulkActions={BULK_ACTIONS}
            selectable
            exportable
            onRowClick={(t: any) => { setSelectedTenant(t); setPanelTab('overview'); }}
            activeRowId={selectedTenant?._id ?? null}
            onPanelClose={() => setSelectedTenant(null)}
            onOpenFullPage={selectedTenant ? () => navigate(`/tenants/${selectedTenant._id}`) : undefined}
            panelHeader={
              selectedTenant ? (
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1 min-w-0 mr-2">
                    <h2 className="text-base font-semibold truncate text-gray-900">{selectedTenant.name}</h2>
                    <p className="text-xs mt-0.5 text-gray-500">{selectedTenant.slug}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleEnterDepartment(selectedTenant)}
                      disabled={enteringDept === selectedTenant._id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors disabled:opacity-50"
                      title="Enter Tenant Web"
                    >
                      <Monitor className="h-3.5 w-3.5" />
                      Web
                    </button>
                    <button
                      onClick={() => handleEnterMobile(selectedTenant)}
                      disabled={enteringMobile === selectedTenant._id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors disabled:opacity-50"
                      title="Enter Tenant Mobile"
                    >
                      <Smartphone className="h-3.5 w-3.5" />
                      Mobile
                    </button>
                    <div className="w-px h-4 bg-gray-200 mx-0.5" />
                    <button
                      onClick={() => navigate(`/tenants/${selectedTenant._id}`)}
                      className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-400"
                      title="Open full page"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setSelectedTenant(null)}
                      className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-400"
                      title="Close panel"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : undefined
            }
            panelDefaultWidth={580}
            panelContent={
              selectedTenant ? (
                <DepartmentPanel
                  tenant={selectedTenant}
                  activeTab={panelTab}
                  onTabChange={setPanelTab}
                  onSave={handlePanelSave}
                  onExtend={handlePanelExtend}
                  onConvert={handlePanelConvert}
                  onCleanup={handlePanelCleanup}
                />
              ) : null
            }
            seedTemplates={SEED_TEMPLATES}
            onRefresh={loadTenants}
            emptyIcon={Building2}
            emptyTitle="No departments yet"
            emptySubtitle="Create your first department to get started"
            emptyCTA={{ label: 'New Department', onClick: () => navigate('/tenants/new') }}
            searchPlaceholder="Search departments..."
            toolbarActions={
              <button
                onClick={() => navigate('/tenants/new')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
              >
                <Plus className="h-4 w-4" /> New Department
              </button>
            }
          />
        </div>
      )}

      {/* ── Trials tab ── */}
      {tab === 'trials' && (
        <div className="flex-1 min-h-0">
          <GridPageShell
            ref={trialsGridRef}
            gridId="trials"
            gridType="trials"
            title="Trials"
            subtitle="Active trial departments"
            hideHeader
            columns={TRIAL_COLUMNS}
            data={trials}
            getRowId={(t: any) => t._id}
            rowActions={TRIAL_ROW_ACTIONS}
            onRowClick={(row: any) => setSelectedTrial(row)}
            activeRowId={selectedTrial?._id ?? null}
            onPanelClose={() => setSelectedTrial(null)}
            panelTitle={selectedTrial?.name}
            panelSubtitle={
              selectedTrial
                ? selectedTrial._trialExpired
                  ? 'Trial Expired'
                  : `${selectedTrial._trialDaysRemaining ?? 0}d remaining`
                : undefined
            }
            panelDefaultWidth={520}
            panelContent={
              selectedTrial ? (
                <TrialPanel
                  tenant={selectedTrial}
                  onExtend={handlePanelExtend}
                  onConvert={handlePanelConvert}
                  onCleanup={handlePanelCleanup}
                />
              ) : null
            }
            emptyIcon={Clock}
            emptyTitle="No active trials"
            emptySubtitle="Trial departments will appear here"
            searchPlaceholder="Search trials..."
            onRefresh={async () => {
              const fresh = await syncTrialsFromServer();
              setTrials(fresh);
            }}
          />
        </div>
      )}

      {/* ── Drafts tab ── */}
      {tab === 'drafts' && (
        <div className="flex-1 min-h-0">
          <GridPageShell
            ref={draftsGridRef}
            gridId="drafts"
            gridType="drafts"
            title="Drafts"
            subtitle="Saved onboarding wizard drafts"
            hideHeader
            columns={DRAFT_COLUMNS}
            data={drafts}
            getRowId={(d: any) => d._id}
            rowActions={DRAFT_ROW_ACTIONS}
            onRowClick={(row: any) => setSelectedDraft(row)}
            activeRowId={selectedDraft?._id ?? null}
            onPanelClose={() => setSelectedDraft(null)}
            panelTitle={selectedDraft?.wizardState?.departmentInfo?.name || 'Untitled Draft'}
            panelSubtitle={selectedDraft ? `Step ${selectedDraft.lastStep} of 9` : undefined}
            panelDefaultWidth={520}
            panelContent={
              selectedDraft ? (
                <DraftPanel
                  draft={selectedDraft}
                  onResume={(id) => navigate(`/tenants/new/${id}`)}
                  onDelete={async (draft) => {
                try {
                  await deleteOnboardingDraft(draft._id);
                  setSelectedDraft(null);
                  loadDrafts();
                } catch (err: any) {
                  setActionError(err.message || 'Failed to delete draft');
                }
              }}
                />
              ) : null
            }
            emptyIcon={FileEdit}
            emptyTitle="No drafts"
            emptySubtitle="Drafts are auto-saved when you start the onboarding wizard"
            searchPlaceholder="Search drafts..."
            onRefresh={loadDrafts}
            toolbarActions={
              <div className="flex items-center gap-2">
                {drafts.length > 0 && (
                  <button
                    onClick={() => setClearingDrafts(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" /> Clear All Drafts
                  </button>
                )}
                <button
                  onClick={() => navigate('/tenants/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
                >
                  <Plus className="h-4 w-4" /> New Department
                </button>
              </div>
            }
          />
        </div>
      )}

      {/* ── Edit Department ── */}
      {editTarget && (
        <EditDepartmentModal
          tenant={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setTenants((ts) => ts.map((t) => t._id === updated._id ? updated : t));
            if (selectedTenant?._id === updated._id) setSelectedTenant(updated);
            setEditTarget(null);
          }}
        />
      )}

      {/* ── Suspend Department ── */}
      {suspendTarget && (
        <ConfirmActionModal
          title="Suspend Department?"
          description={<>Suspend <strong>{suspendTarget.name}</strong>? Users will lose access until the department is reactivated.</>}
          confirmButtonLabel="Yes, Suspend"
          variant="warning"
          onConfirm={handleSuspend}
          onClose={() => setSuspendTarget(null)}
        />
      )}

      {/* ── Activate Department ── */}
      {activateTarget && (
        <ConfirmActionModal
          title="Activate Department?"
          description={<>Reactivate <strong>{activateTarget.name}</strong>? Users will regain access immediately.</>}
          confirmButtonLabel="Yes, Activate"
          variant="default"
          onConfirm={handleActivate}
          onClose={() => setActivateTarget(null)}
        />
      )}

      {/* ── Bulk Suspend ── */}
      {bulkSuspendTargets && (
        <ConfirmActionModal
          title={`Suspend ${bulkSuspendTargets.length} Department${bulkSuspendTargets.length !== 1 ? 's' : ''}?`}
          description={<>Suspend the selected {bulkSuspendTargets.length} department{bulkSuspendTargets.length !== 1 ? 's' : ''}? Users will lose access until reactivated.</>}
          confirmButtonLabel="Yes, Suspend All"
          variant="warning"
          onConfirm={handleBulkSuspend}
          onClose={() => setBulkSuspendTargets(null)}
        />
      )}

      {/* ── Extend Trial Modal ── */}
      {extendTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Extend Trial: {extendTarget.name}</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="extend-days" className="block text-sm font-medium text-gray-700 mb-1">Extension (days)</label>
                <input id="extend-days" type="number" min="1" max="90" value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 14)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" title="Extension in days" placeholder="14" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea rows={2} value={extendReason} onChange={(e) => setExtendReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reason for extension..." />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setExtendTarget(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button onClick={handleExtend} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Extend by {extendDays} days</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Convert Trial Confirmation ── */}
      {convertTarget && (
        <ConfirmActionModal
          title="Convert Trial?"
          description={<>Convert <strong>{convertTarget.name}</strong> from trial to active subscription?</>}
          confirmButtonLabel="Yes, Convert"
          variant="warning"
          onConfirm={handleConvert}
          onClose={() => setConvertTarget(null)}
        />
      )}

      {/* ── Cleanup Trial Confirmation ── */}
      {cleanupTarget && (
        <ConfirmActionModal
          title="Clean Up Trial?"
          description={<>Clean up expired trial <strong>{cleanupTarget.name}</strong>? This will suspend the tenant.</>}
          confirmButtonLabel="Yes, Clean Up"
          variant="danger"
          onConfirm={handleCleanup}
          onClose={() => setCleanupTarget(null)}
        />
      )}

      {/* ── Delete Department ── */}
      {deleteTarget && (
        <ConfirmDeleteModal
          title="Delete Department?"
          confirmValue={deleteTarget.name}
          confirmLabel="department name"
          description={<>Permanently delete <strong>{deleteTarget.name}</strong>? This will remove all associated data and cannot be undone.</>}
          deleteButtonLabel="Delete Department"
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* ── Bulk Delete Departments ── */}
      {bulkDeleteTargets && (
        <ConfirmActionModal
          title={`Delete ${bulkDeleteTargets.length} Department${bulkDeleteTargets.length !== 1 ? 's' : ''}?`}
          description={<>Permanently delete {bulkDeleteTargets.length} department{bulkDeleteTargets.length !== 1 ? 's' : ''}? This cannot be undone.</>}
          confirmButtonLabel={`Delete ${bulkDeleteTargets.length} Department${bulkDeleteTargets.length !== 1 ? 's' : ''}`}
          variant="danger"
          onConfirm={handleBulkDelete}
          onClose={() => setBulkDeleteTargets(null)}
        />
      )}

      {/* ── Clear All Drafts Confirmation ── */}
      {clearingDrafts && (
        <ConfirmActionModal
          title={`Clear All Drafts?`}
          description={<>Permanently delete all <strong>{drafts.length}</strong> draft{drafts.length !== 1 ? 's' : ''}? This cannot be undone.</>}
          confirmButtonLabel={`Delete ${drafts.length} Draft${drafts.length !== 1 ? 's' : ''}`}
          variant="danger"
          onConfirm={handleClearAllDrafts}
          onClose={() => setClearingDrafts(false)}
        />
      )}
    </div>
  );
}
